import type { RadioProps } from '@byecode/ui'
import { Checkbox, Radio } from '@byecode/ui'
import type { AlignMent, FieldBreakPointConfigProtocol, VariableADTvalue } from '@lighthouse/core'
import { camelToKebab } from '@lighthouse/tools'
import React, { useCallback, useMemo } from 'react'
import styled, { css } from 'styled-components'

import type { ThemeDesignInitConfig } from '../../../../../constants'
import { themeFieldDesignConfig } from '../../../../../constants'
import { getBackgroundStyle, getBorderStyle, getTextColorStyle } from '../../../../../utils'
import { useAppContainerContext, useAppThemeContext } from '../../../../ApplicationContainer'
import { alignMap, directionMap } from '../../../constant'
import { Item } from './Item'

type Option = {
    label: string
    value: string
    color?: string
}

interface CheckBoxGroupProps {
    value?: string[]
    options: Option[]
    breakPoint?: FieldBreakPointConfigProtocol
    direction?: 'vertical' | 'horizontal'
    align?: AlignMent
    isMultiple?: boolean
    disabled?: boolean
    colorless?: boolean
    onChange: (v: string[]) => void
    onRenderLabel?: (value: VariableADTvalue | undefined) => string
}

const SCxGroup = styled.div<{
    radioHoverStyle?: Record<'input' | 'label', React.CSSProperties>
    checkboxHoverStyle?: Record<'inner' | 'label', React.CSSProperties>
}>`
    display: flex;
    gap: 8px;

    ${({ radioHoverStyle }) =>
        radioHoverStyle &&
        css`
            & .byecode-radio-input:hover {
                ${Object.entries(radioHoverStyle.input)
                    .map(([key, value]) => `${camelToKebab(key)}: ${value};`)
                    .join('\n')}
            }
            & .byecode-radio-label:hover {
                ${Object.entries(radioHoverStyle.label)
                    .map(([key, value]) => `${camelToKebab(key)}: ${value};`)
                    .join('\n')}
            }
        `}

    ${({ checkboxHoverStyle }) =>
        checkboxHoverStyle &&
        css`
            & .byecode-checkbox-inner:hover {
                ${Object.entries(checkboxHoverStyle.inner)
                    .map(([key, value]) => `${camelToKebab(key)}: ${value};`)
                    .join('\n')}
            }
            & .byecode-checkbox-label:hover {
                ${Object.entries(checkboxHoverStyle.label)
                    .map(([key, value]) => `${camelToKebab(key)}: ${value};`)
                    .join('\n')}
            }
        `}
`

const SCxContainer = styled.div<Pick<React.CSSProperties, 'flexDirection' | 'alignItems'>>`
    display: flex;
    flex-wrap: wrap;
    ${({ flexDirection, alignItems }) =>
        flexDirection === 'row'
            ? css`
                  flex-direction: row;
                  gap: 8px 12px;
                  justify-content: ${alignItems};
              `
            : css`
                  flex-direction: column;
                  gap: 8px;
                  align-items: ${alignItems};
              `}
`
export const CheckBoxGroup: React.FunctionComponent<CheckBoxGroupProps> = ({
    value = [],
    options,
    breakPoint,
    direction = 'horizontal',
    align = 'left',
    isMultiple,
    colorless,
    disabled,
    onChange,
    onRenderLabel
}) => {
    const { appId } = useAppContainerContext()
    const { theme } = useAppThemeContext()
    const styleTheme = useMemo(() => breakPoint?.design?.theme, [breakPoint?.design?.theme])
    const { design, hoverStyle, activeStyle, disabledStyle } = useMemo(() => {
        if (!styleTheme || styleTheme === 'CUSTOM') {
            return {
                design: breakPoint?.design,
                hoverStyle: breakPoint?.hoverStyle,
                activeStyle: breakPoint?.activeStyle,
                disabledStyle: breakPoint?.disabledStyle
            }
        }
        return themeFieldDesignConfig('relativeSelect', { theme, relativeSelectMode: 'checkbox' })[styleTheme] as ThemeDesignInitConfig
    }, [styleTheme, theme, breakPoint?.design, breakPoint?.hoverStyle, breakPoint?.activeStyle, breakPoint?.disabledStyle])

    const handleChange = useCallback(
        (v: string[]) => {
            onChange(isMultiple ? v : v.slice(-1))
        },
        [isMultiple, onChange]
    )

    // 常规
    const textColorStyle = useMemo(() => getTextColorStyle(design?.color, theme.palettes), [design?.color, theme.palettes])
    const backgroundStyle = useMemo(
        () =>
            getBackgroundStyle({
                appId,
                value: design?.background,
                palettes: theme.palettes,
                parseVariableImage: onRenderLabel,
                cssSuffix: '!important'
            }),
        [appId, design?.background, onRenderLabel, theme.palettes]
    )
    const borderStyle = useMemo(() => getBorderStyle(design?.border, theme.palettes, '!important'), [design?.border, theme.palettes])

    /* 悬浮态(单独) start */
    const hoverTextColorStyle = useMemo(
        () => getTextColorStyle(hoverStyle?.design?.color, theme.palettes),
        [hoverStyle?.design?.color, theme.palettes]
    )
    const hoverBackgroundStyle = useMemo(
        () =>
            getBackgroundStyle({
                appId,
                value: hoverStyle?.design?.background,
                palettes: theme.palettes,
                parseVariableImage: onRenderLabel,
                cssSuffix: '!important'
            }),
        [hoverStyle?.design?.background, appId, onRenderLabel, theme.palettes]
    )
    const hoverBorderStyle = useMemo(
        () => getBorderStyle(hoverStyle?.design?.border, theme.palettes, '!important'),
        [hoverStyle?.design?.border, theme.palettes]
    )
    /* 悬浮态(单独) end */

    /* 选中态(单独) start */
    const activeTextColorStyle = useMemo(
        () => getTextColorStyle(activeStyle?.design?.color, theme.palettes),
        [activeStyle?.design?.color, theme.palettes]
    )
    const activeBackgroundStyle = useMemo(
        () =>
            getBackgroundStyle({
                appId,
                value: activeStyle?.design?.background,
                palettes: theme.palettes,
                parseVariableImage: onRenderLabel,
                cssSuffix: '!important'
            }),
        [activeStyle?.design?.background, appId, onRenderLabel, theme.palettes]
    )
    const activeBorderStyle = useMemo(
        () => getBorderStyle(activeStyle?.design?.border, theme.palettes, '!important'),
        [activeStyle?.design?.border, theme.palettes]
    )
    const activeCheckedColorStyle = useMemo(
        () => getTextColorStyle(activeStyle?.design?.checkedColor, theme.palettes).color || 'var(--color-app-main)',
        [activeStyle?.design?.checkedColor, theme.palettes]
    )
    /* 选中态(单独) end */

    /* 禁用态(单独) start */
    const disabledTextColorStyle = useMemo(
        () => getTextColorStyle(disabledStyle?.design?.color, theme.palettes),
        [disabledStyle?.design?.color, theme.palettes]
    )
    const disabledBackgroundStyle = useMemo(
        () =>
            getBackgroundStyle({
                appId,
                value: disabledStyle?.design?.background,
                palettes: theme.palettes,
                parseVariableImage: onRenderLabel,
                cssSuffix: '!important'
            }),
        [disabledStyle?.design?.background, appId, onRenderLabel, theme.palettes]
    )
    const disabledBorderStyle = useMemo(
        () => getBorderStyle(disabledStyle?.design?.border, theme.palettes, '!important'),
        [disabledStyle?.design?.border, theme.palettes]
    )
    /* 禁用态(单独) end */

    const radioHoverStyle = useCallback(
        (checked: boolean) => {
            if (disabled || checked || !hoverStyle?.open) {
                return undefined
            }
            return {
                input: {
                    ...hoverBackgroundStyle,
                    ...hoverBorderStyle
                },
                label: {
                    ...hoverTextColorStyle
                }
            }
        },
        [disabled, hoverBackgroundStyle, hoverBorderStyle, hoverStyle?.open, hoverTextColorStyle]
    )

    const checkboxHoverStyle = useCallback(
        (checked: boolean) => {
            if (disabled || checked || !hoverStyle?.open) {
                return undefined
            }
            return {
                inner: {
                    ...hoverBorderStyle,
                    ...hoverBackgroundStyle
                },
                label: {
                    ...hoverTextColorStyle
                }
            }
        },
        [disabled, hoverBackgroundStyle, hoverBorderStyle, hoverStyle?.open, hoverTextColorStyle]
    )

    // 常规和选中态
    const radioStyle = useCallback(
        (checked: boolean): RadioProps['styles'] => {
            if (disabled && disabledStyle?.open) {
                return {
                    root: {
                        opacity: 1
                    },
                    input: {
                        ...disabledBackgroundStyle,
                        width: '16px!important',
                        height: '16px!important',
                        ...disabledBorderStyle
                    },
                    icon: {
                        width: '8px!important',
                        height: '8px!important',
                        left: '4px!important',
                        top: '4px!important'
                    },
                    label: {
                        ...disabledTextColorStyle
                    }
                }
            }
            if (checked && activeStyle?.open) {
                return {
                    input: {
                        ...activeBackgroundStyle,
                        width: '16px!important',
                        height: '16px!important',
                        ...activeBorderStyle
                        // borderWidth: checked ? 2 : 1,
                        // borderColor: `${checked ? checkedColor || 'var(--color-app-main)' : 'var(--color-theme-4)'}!important`
                    },
                    icon: {
                        width: '8px!important',
                        height: '8px!important',
                        left: '4px!important',
                        top: '4px!important'
                    },
                    label: {
                        ...activeTextColorStyle
                    }
                }
            }
            return {
                input: {
                    ...backgroundStyle,
                    width: '16px!important',
                    height: '16px!important',
                    ...borderStyle
                },
                icon: {
                    width: '8px!important',
                    height: '8px!important',
                    left: '4px!important',
                    top: '4px!important'
                },
                label: {
                    ...textColorStyle
                }
            }
        },
        [
            activeBackgroundStyle,
            activeBorderStyle,
            activeStyle?.open,
            activeTextColorStyle,
            backgroundStyle,
            borderStyle,
            disabled,
            disabledBackgroundStyle,
            disabledBorderStyle,
            disabledStyle?.open,
            disabledTextColorStyle,
            textColorStyle
        ]
    )

    const checkboxStyle = useCallback(
        (checked: boolean) => {
            if (disabled && disabledStyle?.open) {
                return {
                    inner: {
                        backgroundColor: 'transparent!important',
                        ...disabledBorderStyle,
                        ...disabledBackgroundStyle
                    },
                    label: {
                        ...disabledTextColorStyle
                    },
                    icon: {
                        color: activeCheckedColorStyle || ''
                    }
                }
            }
            if (checked && activeStyle?.open) {
                return {
                    inner: {
                        backgroundColor: 'transparent!important',
                        ...activeBorderStyle,
                        ...activeBackgroundStyle
                    },
                    label: {
                        ...activeTextColorStyle
                    },
                    icon: {
                        color: activeCheckedColorStyle || ''
                    }
                }
            }
            return {
                inner: {
                    backgroundColor: 'transparent!important',
                    ...borderStyle,
                    ...backgroundStyle
                },
                label: {
                    ...textColorStyle
                },
                icon: {
                    color: 'var(--color-theme-2)'
                }
            }
        },
        [
            activeBackgroundStyle,
            activeBorderStyle,
            activeCheckedColorStyle,
            activeStyle?.open,
            activeTextColorStyle,
            backgroundStyle,
            borderStyle,
            disabled,
            disabledBackgroundStyle,
            disabledBorderStyle,
            disabledStyle?.open,
            disabledTextColorStyle,
            textColorStyle
        ]
    )

    return (
        <SCxContainer flexDirection={directionMap[direction]} alignItems={alignMap[align]}>
            {options.map(option => {
                const checked = Boolean(value?.includes(option.value))
                return (
                    <SCxGroup
                        key={option.value}
                        radioHoverStyle={radioHoverStyle(checked)}
                        checkboxHoverStyle={checkboxHoverStyle(checked)}
                        onMouseDown={() =>
                            !disabled && handleChange(checked ? value?.filter(v => v !== option.value) : [...value, option.value])
                        }
                    >
                        {isMultiple ? (
                            <Checkbox
                                checked={checked}
                                color={activeCheckedColorStyle}
                                disabled={disabled}
                                styles={checkboxStyle(checked)}
                                size="xs"
                                value={option.value}
                            />
                        ) : (
                            <Radio
                                size="xs"
                                // styles={{
                                //     radio: {
                                //         backgroundColor: 'var(--color-white)',
                                //         borderRadius: '100%'
                                //     },
                                //     input: {
                                //         width: '16px!important',
                                //         height: '16px!important',
                                //         borderWidth: checked ? 2 : 1,
                                //         borderColor: `${checked ? 'var(--color-app-main)' : 'var(--color-theme-4)'}!important`
                                //     },
                                //     icon: checked
                                //         ? {
                                //               width: '8px!important',
                                //               height: '8px!important',
                                //               left: '4px!important',
                                //               top: '4px!important'
                                //           }
                                //         : {
                                //               width: '14px!important',
                                //               height: '14px!important',
                                //               left: '1px!important',
                                //               top: '1px!important'
                                //           }
                                // }}
                                disabled={disabled}
                                styles={radioStyle(checked)}
                                label={colorless ? option.label : ''}
                                style={{ padding: colorless ? '0 8px' : 0, gap: colorless ? 8 : 0 }}
                                color={checked ? activeCheckedColorStyle : ''}
                                checked={checked}
                            />
                        )}
                        {!colorless && <Item key={option.value} colorless={colorless} data={option} />}
                    </SCxGroup>
                )
            })}
        </SCxContainer>
    )
}
